import {Skin} from "@pixi-spine/runtime-3.8";
import {MiniSignal, selectRandom, spine} from "p3";
import BossFightScene from "../../../scenes/BossFightScene";
import Entity from "../../Entity";

export default class BossIvandoe extends Entity {
    constructor() {
        super();

        this._attack = 120;
        this._healing = 95;
        this._miss = 80;

        this._minDamage = 4; 
        this._maxDamage = 8; 
        this._health = 12;
        this._maxHealth = 12;

        this._idleAnimations = [
            "boss_idle_sword_00"
        ];

        this._attackAnimations = [
            "boss_attack_r_sword_00",
            "boss_attack_r_sword_01"
        ];

        this._takeDamageAnimations = [
            "boss_hurt_r_sword_00",
            "boss_hurt_r_sword_01",
            "boss_hurt_r_sword_02"
        ];

        this._healAnimations = [
            "boss_eat_r_sword_00"
        ];

        this._walkAnimations = [
            {body:"body_walk_r_sick_00", legs:"legs_walk_r_00"}
        ];

        this._runAnimations = [
            {body:"body_run_r_00", legs:"legs_run_r_00"}
        ];

        this._deathAnimations = [
            "die_r_00"
        ];

        this._animationNames = {
            walk_sick: [this._walkAnimations[0]]
        };

        this._skinNormal = null;
        this._skinCombat = null;

        this._damageEffectFilter = null;
        this._damageEffectTween = null;

        this.signals.enter = new MiniSignal();
        this.signals.attack = new MiniSignal();
        this.signals.takeDamage = new MiniSignal();
        this.signals.damageDelt = new MiniSignal();
        this.signals.dead = new MiniSignal();
    }

    init() {
        super.init();

        this._damageEffectFilter = new P3.filters.AdditiveFilter(0, 3);

        this.addViewWithSpine(spine("char_ivandoe"));

        this._skinNormal = new Skin("ivandoe_stance_normal");
        this._skinNormal.addSkin(this._view.spineData.findSkin("normal"));
        this._skinNormal.addSkin(this._view.spineData.findSkin("equip_sword"));
        this._skinNormal.addSkin(this._view.spineData.findSkin("outfit_standard"));

        this._skinCombat = new Skin("ivandoe_stance_combat");
        this._skinCombat.addSkin(this._view.spineData.findSkin("normal"));
        this._skinCombat.addSkin(this._view.spineData.findSkin("equip_sword"));
        this._skinCombat.addSkin(this._view.spineData.findSkin("outfit_standard"));

        this._view.stateData.defaultMix = 0.1;
        this._view.scale.set(0.245 * this.facingDirection, 0.245);

        this._startRunAnimation();
        
        this._isIdleAnimation = false;
        this._isAttackAnimation = false;
        this._isTakeDamageAnimation = false;

        this.signals.enter.add(this._startRunAnimation, this);
        this.signals.attack.add(this._startAttack, this);
        this.signals.takeDamage.add(this._startTakeDamage, this);
    }

    destroy() {
        P3.utils.destroyAllSignals(this.signals);

        super.destroy();
    }

    onViewAdded() {
        super.onViewAdded();
    }

    update() {
        super.update();

        this._view && this._view.update(window.dt);
    }

    _removeDamageEffect() {
        if (this._damageEffectTween) {
            BossFightScene.active.removeTween(this._damageEffectTween, true);
            this._damageEffectTween = null;
        }

        this._damageEffectFilter.blendStrength = 0;
        this.filters = [];
    }

    /**
     * @return {Promise}
     */
    _damageEffect() {
        return new Promise((resolve) => {
            this._removeDamageEffect();

            this._damageEffectFilter.blendStrength = 0.3;
            this._damageEffectTween = BossFightScene.active.addTween(gsap.to(this._damageEffectFilter, 0.24, {
                blendStrength: 0,
                ease: Power2.easeOut,
                onStart: () => {
                    this.filters = [this._damageEffectFilter]
                },
                onComplete: () => {
                    this.filters = [];
                    resolve();
                }
            }));
        });
    }
    
    playAnimation(name, loop, additive) {
        if (Array.isArray(name) ) {
            name = selectRandom(name);
        }
        if (typeof name == "string") {
            super.playAnimation(name, loop, additive);
            return;
        }
        ////console.log(name)
        if (name.body) {
            super.playAnimation(name.body, loop, additive, 0);
        }
        if (name.legs) {
            super.playAnimation(name.legs, loop, additive, 1);
        }
    }

    playIdleAnimation() {
        if (this.isAttacking || this.isTakingDamage || this.isHealing) {
            return;
        }

        this._view.state.clearTrack(1);
        this.playAnimation(this._idleAnimations);
    }

    playDeathAnimation() {
        P3.audio.playSound(['vo_bert_gasp_00', 'vo_bert_err_00']);
        P3.audio.playSound(['vo_iv_faint_00', 'vo_iv_faint_01']);
        this.playAnimation(this._deathAnimations);
    }

    playWinAnimation() {
        this.playAnimation("boss_win_sword_00");
    }

    playHealAnimation() {
        this.playAnimation(this._healAnimations);
    }

    playGiveInAnimation() {
        this.playAnimation(this._animationNames['walk_sick'], true);
    }

    _startAttack() {
        this.setSkin(this._skinCombat);
        this.playAnimation(this._attackAnimations, false);
    }

    _startRunAnimation() {
        this.setSkin(this._skinNormal);
        this.playAnimation(this._runAnimations, true);
    }

    _startTakeDamage(amount) {
        if(this.isDead) {
            return;
        }

        this._damageEffect();

        this._health -= amount;

        this.playAnimation(this._takeDamageAnimations);
    }

    /**
     * @param {string} name
     * @param entry
     */
    onAnimationStart(name, entry) {
        this._isIdleAnimation = this._idleAnimations.indexOf(name) >= 0;
        this._isAttackAnimation = this._attackAnimations.indexOf(name) >= 0;
        this._isTakeDamageAnimation = this._takeDamageAnimations.indexOf(name) >= 0;
        this._isHealingAnimation = this._healAnimations.indexOf(name) >= 0;
    }

    onAnimationComplete(name, entry) {
        if (this._deathAnimations.indexOf(name) >= 0) {
            P3.audio.playSound("vo_iv_asleep_loop_00");
        }
        if (this._idleAnimations.indexOf(name) >= 0) {
            this.playIdleAnimation();
        }
        if (this._attackAnimations.indexOf(name) >= 0) {
            this._isAttackAnimation = false;
            this.playIdleAnimation();
        }
        if (this._healAnimations.indexOf(name) >= 0) {
            this._isHealingAnimation = false;
            this.playIdleAnimation();
        }
        if (this._takeDamageAnimations.indexOf(name) >= 0) {
            this._isTakeDamageAnimation = false;
            if(this.isDead) {
                this.signals.dead.dispatch(); 
            }
            
            this.playIdleAnimation();
        }
    }

    onAnimationEvent(entry, event) {
        if (event.stringValue === "event_attack") {
            P3.audio.playSound(['vo_iv_effort_00', 'vo_iv_effort_01', 'vo_iv_effort_02', 'vo_iv_effort_03', 'vo_iv_effort_04', 'vo_iv_effort_05']);
            P3.audio.playSound(['sfx_action_sword_swipe_00', 'sfx_action_sword_swipe_01', 'sfx_action_sword_swipe_02', 'sfx_action_sword_swipe_03']);
        }
        if (event.stringValue === "event_strike_boss") {
            this.signals.damageDelt.dispatch();
        }
        if (event.stringValue === "sfx_hurt") {
            P3.audio.playSound(['vo_iv_hurt_00', 'vo_iv_hurt_01', 'vo_iv_hurt_02', 'vo_iv_hurt_03', 'vo_iv_hurt_04', 'vo_iv_hurt_05', 'vo_iv_hurt_06', 'vo_iv_hurt_07']);
            P3.audio.playSound(['sfx_ivandoe_take_damage_00', 'sfx_ivandoe_take_damage_01', 'sfx_ivandoe_take_damage_02', 'sfx_ivandoe_take_damage_03', 'sfx_ivandoe_take_damage_04']);
        }
        if(event.stringValue === "sfx_eat") {
            P3.audio.playSound("sfx_ivandoe_eat");
        } 
    }

    get attack() {
        return this._attack
    }

    get healing() {
        return this._healing;
    }

    get miss() {
        return this._miss;
    }

    get isDead() {
        return this._health <= 0;
    }

    get health() {
        return this._health;
    }

    set health(value) {
        this._health = value;
    }

    get maxHealth() {
        return this._maxHealth;
    }

    get minDamage() {
        return this._minDamage;
    }

    get maxDamage() {
        return this._maxDamage;
    }

    get isAttacking() {
        return this._isAttackAnimation;
    }

    get isTakingDamage() {
        return this._isTakeDamageAnimation;
    }

    get isHealing() {
        return this._isHealingAnimation;
    }
}